import * as axios from 'axios';
import { _404 } from './handlers';
import { IConfiguredHttp } from './http';

interface IUploadConfig {
	data: FormData;
	onUploadProgress?(progressEvent: any): any;
}

interface IImageResolutions {
	'210x210': string;
	'400x400': string;
	'1024x768': string;
	'1920x1080': string;
	[key: string]: string;
}

export interface IImageDeleteResponse {
	success: Array<{ key: string }>;
	error: Array<{ key: string, error: string }>;
}

export interface IImage {
	id: string;
	sort_order: number;
	caption: string;
	is_active: boolean;
	original: keyof IImageResolutions;
	formats: {
		[key in keyof IImageResolutions]: IImageFormat;
	};
	quality_score: IImageScore | null;
}

export interface IImageFormat {
	id: string;
	name: string;
	x_dimension: string;
	y_dimension: string;
	url: string;
}

export interface IImageScore {
	aesthetic: number;
	technical: number;
	tags: IImageObjectTag[];
}

export interface IImageObjectTag {
	object: string;
	confidence: number;
}

export interface IImagesSummary {
	[key: string]: { lead_url: string, count: number };
}

export interface IImageRegisterPayload {
	width: number;
	height: number;
	name: string;
}

export interface IS3PresignedPost {
	url: string;
	fields: {
		Key: string;
		bucket: string;
		Policy: string;
		acl?: string;
		'Content-Type'?: string;
		file?: File;
	};
}

export interface IImagesApi {
	fetchImageSummariesByPropertyIds(properties: string[]): Promise<IImagesSummary>;
	getByPropertyId(id: string): Promise<IImage[]>;
	uploadByPropertyId(id: string, config: IUploadConfig): Promise<IImage[]>;
	setImageOrder(id: string, images: IImage[]): Promise<IImage[]>;
	update(id: string, images: Array<Partial<IImage>>): Promise<IImage[]>;
	deleteImages(propertyId: string, images: IImage[]): Promise<IImage[]>;
	generateZip(propertyId: string | number): Promise<{ url: string }>;
	registerForUpload(propertyId: string, payload: IImageRegisterPayload[]): Promise<Array<{ id: string; presigned: IS3PresignedPost }>>;
	uploadToBucket(postRequest: IS3PresignedPost, onUploadProgress?: (progressEvent: any) => any): Promise<any>;
	resizePropertyImages(propertyId: string | number): Promise<void>;
}

const imagesApiFactory = (http: IConfiguredHttp, prefix = ''): IImagesApi => ({
	fetchImageSummariesByPropertyIds(properties) {
		return http.post(`${prefix}/properties`, properties).catch(_404([]));
	},
	getByPropertyId(id) {
		return http.get(`${prefix}/property/${id}/details`).catch(_404([]));
	},
	uploadByPropertyId(id, config) {
		return http.post(`${prefix}/property/${id}/upload`, config.data, {
			...config,
			headers: {
				'Content-Type': 'multipart/form-data',
			}
		});
	},
	setImageOrder(propertyId, images) {
		const payload = images.map((image): Pick<IImage, 'id' | 'sort_order'> => ({ id: image.id, sort_order: image.sort_order }));
		return http.patch(`${prefix}/property/${propertyId}`, payload);
	},
	update(propertyId, images) {
		return http.patch(`${prefix}/property/${propertyId}`, images);
	},
	deleteImages(propertyId, images) {
		const forDeletion = images.map((i): Pick<IImage, 'id' | 'is_active'> => ({ id: i.id, is_active: false }));
		return http.patch(`${prefix}/property/${propertyId}`, forDeletion);
	},
	generateZip(propertyId) {
		return http.get(`${prefix}/zip?propertyId=${propertyId}`);
	},
	registerForUpload(propertyId, files) {
		return http.post(`${prefix}/upload/property/${propertyId}`, { property: propertyId, files });
	},
	uploadToBucket(postRequest, onUploadProgress) {
		const { url, fields } = postRequest;
		const data = new FormData();
		Object.keys(fields).forEach((key) => data.append(key, fields[key]));
		return axios.default({
			url,
			data,
			method: 'POST',
			headers: { 'Content-Type': 'multipart/form-data' },
			...(onUploadProgress ? { onUploadProgress } : null)
		});
	},
	resizePropertyImages(property) {
		return http.put(`${prefix}/property/${property}/resize`);
	}
});

export default imagesApiFactory;
